import { ProLabel } from '~/components/pro-label';
import { Checkbox, CheckboxField, CheckboxGroup } from '@oysterjs/ui/checkbox';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import type { ValidationError } from '@oysterjs/core/graphql';

import {
  VoiceAgentAccountLookupDataType,
  type VoiceAgentConfigurationAccountLookupInput
} from '~/api/graphql';

const ACCOUNT_LOOKUP_DATA_TYPE_OPTIONS: {
  value: VoiceAgentAccountLookupDataType;
  label: string;
  description: string;
}[] = [
  {
    value: VoiceAgentAccountLookupDataType.AccountDetails,
    label: 'Account Details',
    description: 'Names and IDs of accounts associated with the caller in Cara.'
  },
  {
    value: VoiceAgentAccountLookupDataType.PolicyDetails,
    label: 'Policy Details',
    description:
      'Policy carrier, premium, and effective dates from your synced AMS integrations, plus related integration account and contact context for the caller.'
  },
  {
    value: VoiceAgentAccountLookupDataType.ServiceRequests,
    label: 'Open Service Tickets',
    description:
      'Recent open service requests (and the most recently closed one) on the caller’s accounts.'
  },
  {
    value: VoiceAgentAccountLookupDataType.PreviousCalls,
    label: 'Previous Calls',
    description: 'Summary of the caller’s most recent past call.'
  },
  {
    value: VoiceAgentAccountLookupDataType.ClientNotes,
    label: 'Client Notes',
    description:
      'Notes may contain confidential information about your clients. Only enable if you are comfortable sharing those during live calls.'
  }
];

type VoiceAccountLookupSectionProps = {
  accountLookup: VoiceAgentConfigurationAccountLookupInput;
  canUseAnswerPolicyQuestion: boolean;
  onChange: (accountLookup: VoiceAgentConfigurationAccountLookupInput) => void;
  getFieldId: (path: (string | number)[]) => string;
  getValidationError: (errors: ValidationError[], path: string[]) => string | undefined;
  validationErrors: ValidationError[];
};

export const VoiceAccountLookupSection = (props: VoiceAccountLookupSectionProps) => {
  const lookupEnabled = props.accountLookup.enabled;

  return (
    <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-6 sm:px-6">
        <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
          <div>
            <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
              Account Lookup
            </h4>
            <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
              Control what account, policy, and service information Cara may read about a caller
              during a live call via the lookup_customer tool.
            </p>
            <p className="mt-3 text-sm/6 text-neutral-600 dark:text-neutral-400">
              The Answer Policy Question tool
              {!props.canUseAnswerPolicyQuestion && <ProLabel />} uses this lookup configuration
              plus full AMS integration records (including raw properties) to answer detailed caller
              questions during the call. It requires a Pro or Enterprise plan.
            </p>
          </div>

          <Fieldset className="flex flex-col gap-y-6 col-span-2">
            <FieldGroup>
              <SwitchField
                data-field-id={props.getFieldId(['configuration', 'accountLookup', 'enabled'])}
              >
                <Switch
                  color="sky"
                  checked={props.accountLookup.enabled}
                  onChange={(enabled) => {
                    props.onChange({
                      ...props.accountLookup,
                      enabled
                    });
                  }}
                />
                <Label>Enable Account Lookup</Label>
                <Description>
                  Allow Cara to look up the caller’s account, policy, and service information when
                  answering a call. Required for Cara to read any customer context.
                </Description>
              </SwitchField>

              <Field
                data-field-id={props.getFieldId([
                  'configuration',
                  'accountLookup',
                  'includedDataTypes'
                ])}
              >
                <Label>Information Cara Can Read</Label>
                <Description>
                  Categories of account context Cara is allowed to read on the call. Anything
                  unchecked is withheld from Cara even when Account Lookup is enabled.
                </Description>
                <CheckboxGroup>
                  {ACCOUNT_LOOKUP_DATA_TYPE_OPTIONS.map((option) => (
                    <CheckboxField key={option.value} disabled={!lookupEnabled}>
                      <Checkbox
                        color="sky"
                        disabled={!lookupEnabled}
                        checked={props.accountLookup.includedDataTypes.includes(option.value)}
                        onChange={(checked) => {
                          const current = props.accountLookup.includedDataTypes;
                          const includedDataTypes = checked
                            ? [...current.filter((item) => item !== option.value), option.value]
                            : current.filter((item) => item !== option.value);
                          props.onChange({
                            ...props.accountLookup,
                            includedDataTypes
                          });
                        }}
                      />
                      <Label>{option.label}</Label>
                      <Description>{option.description}</Description>
                    </CheckboxField>
                  ))}
                </CheckboxGroup>
                {!!props.getValidationError(props.validationErrors, [
                  'configuration',
                  'accountLookup',
                  'includedDataTypes'
                ]) && (
                  <ErrorMessage>
                    {props.getValidationError(props.validationErrors, [
                      'configuration',
                      'accountLookup',
                      'includedDataTypes'
                    ])}
                  </ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </div>
      </div>
    </div>
  );
};
